import { useState } from 'react'
import { useCustomerTrackerStore } from '@/stores/customer-tracker-store'
import { CustomerCard } from './CustomerCard'
import { AddCustomerButton } from './AddCustomerButton'
import { GroupBox } from './GroupBox'
import { Square } from 'lucide-react'

export function CustomerTracker() {
  const { customers, groupBoxes, drawMode, toggleDrawMode, addGroupBox } = useCustomerTrackerStore()
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null)
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null)
  const [showLabelDialog, setShowLabelDialog] = useState(false)
  const [pendingBox, setPendingBox] = useState<{ position: { x: number; y: number }; size: { width: number; height: number } } | null>(null)
  const [groupLabel, setGroupLabel] = useState('')

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawMode) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setIsDrawing(true)
    setDrawStart({ x, y })
    setDrawCurrent({ x, y })
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawMode || !isDrawing || !drawStart) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setDrawCurrent({ x, y })
  }

  const handleMouseUp = (_e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawMode || !isDrawing || !drawStart || !drawCurrent) return

    const width = Math.abs(drawCurrent.x - drawStart.x)
    const height = Math.abs(drawCurrent.y - drawStart.y)

    if (width > 50 && height > 50) {
      const position = {
        x: Math.min(drawStart.x, drawCurrent.x),
        y: Math.min(drawStart.y, drawCurrent.y),
      }

      // Store pending box and show dialog
      setPendingBox({ position, size: { width, height } })
      setShowLabelDialog(true)
    }

    setIsDrawing(false)
    setDrawStart(null)
    setDrawCurrent(null)
  }

  const handleLabelSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (groupLabel.trim() && pendingBox) {
      const color = '#6b7280'  // Gray color
      addGroupBox(groupLabel.trim(), pendingBox.position, pendingBox.size, color)
      setGroupLabel('')
      setPendingBox(null)
      setShowLabelDialog(false)
      toggleDrawMode()  // Exit draw mode after creating
    }
  }

  const handleCancelLabel = () => {
    setGroupLabel('')
    setPendingBox(null)
    setShowLabelDialog(false)
  }

  // Calculate preview box dimensions
  const previewBox = drawStart && drawCurrent ? {
    x: Math.min(drawStart.x, drawCurrent.x),
    y: Math.min(drawStart.y, drawCurrent.y),
    width: Math.abs(drawCurrent.x - drawStart.x),
    height: Math.abs(drawCurrent.y - drawStart.y),
  } : null

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg relative h-full flex flex-col overflow-hidden">
      <div className="relative flex-1 overflow-auto bg-[#0b0c0d]">
        {/* Buttons in top-right corner */}
        <div className="absolute top-3 right-3 z-20 flex gap-2">
          <button
            onClick={toggleDrawMode}
            className="px-3 py-1.5 rounded text-sm font-semibold transition-opacity flex items-center gap-2 shadow-lg bg-primary text-black hover:opacity-90"
          >
            <Square className="w-4 h-4" />
            Add Group
          </button>
          <AddCustomerButton />
        </div>
        <div
          className="relative w-full min-h-full p-3"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{
            cursor: drawMode ? 'crosshair' : 'default',
          }}
        >
          {/* Group boxes layer (behind customer cards) */}
          {groupBoxes.map((box) => (
            <GroupBox key={box.id} box={box} />
          ))}

          {/* Preview box while drawing */}
          {drawMode && previewBox && (
            <div
              className="absolute border-2 border-dashed border-blue-400/50 bg-blue-500/10 pointer-events-none"
              style={{
                left: previewBox.x,
                top: previewBox.y,
                width: previewBox.width,
                height: previewBox.height,
              }}
            />
          )}

          {/* Customer cards layer */}
          {customers.map((customer) => (
            <CustomerCard key={customer.id} customer={customer} drawMode={drawMode} />
          ))}
        </div>
      </div>

      {/* Group Label Dialog */}
      {showLabelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#1a1b1d] border border-white/[0.1] rounded-lg p-4 w-[300px] shadow-xl">
            <form onSubmit={handleLabelSubmit} className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-white">Name this group</h3>
              <input
                type="text"
                value={groupLabel}
                onChange={(e) => setGroupLabel(e.target.value)}
                placeholder="e.g., Hot Leads"
                autoFocus
                className="bg-white/[0.05] text-white px-3 py-2 rounded border border-white/[0.1] focus:border-primary focus:outline-none text-sm placeholder:text-white/40"
              />
              <div className="flex gap-2">
                <button type="submit" className="bg-primary text-black px-4 py-2 rounded hover:opacity-90 transition-opacity text-sm font-semibold flex-1">
                  Create
                </button>
                <button type="button" onClick={handleCancelLabel} className="bg-white/[0.05] border border-white/[0.1] text-white px-4 py-2 rounded hover:bg-white/[0.08] transition-colors text-sm font-medium">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
